// Comprehensive Brain Naming Data
// English names + Latin names following Terminologia Anatomica

// Distance (meters) each hemisphere translates along X when the brain splits.
// Left hemisphere -> +X, Right hemisphere -> -X. Shared with anatomyRegistry.js
// so the search-focus pipeline can pre-compute post-split target coordinates.
export const SPLIT_DISTANCE = 0.12;

export const BRAIN_NAMES = {
  "Amygdala_L": { english: "Amygdala (Left)", latin: "Corpus Amygdaloideum (Sinister)" },
  "Amygdala_R": { english: "Amygdala (Right)", latin: "Corpus Amygdaloideum (Dexter)" },
  "Anterior commissure": { english: "Anterior Commissure", latin: "Commissura Anterior" },
  "Caudate nucleus_L": { english: "Caudate Nucleus (Left)", latin: "Nucleus Caudatus (Sinister)" },
  "Caudate nucleus_R": { english: "Caudate Nucleus (Right)", latin: "Nucleus Caudatus (Dexter)" },
  "Cerebellum_L": { english: "Cerebellum (Left)", latin: "Cerebellum (Sinister)" },
  "Cerebellum_R": { english: "Cerebellum (Right)", latin: "Cerebellum (Dexter)" },
  "Cerebrum_L": { english: "Cerebrum (Left)", latin: "Telencephalon (Sinister)" },
  "Cerebrum_R": { english: "Cerebrum (Right)", latin: "Telencephalon (Dexter)" },
  "Cingulate gyrus_L": { english: "Cingulate Gyrus (Left)", latin: "Gyrus Cinguli (Sinister)" },
  "Cingulate gyrus_R": { english: "Cingulate Gyrus (Right)", latin: "Gyrus Cinguli (Dexter)" },
  "Corpus callosum_L": { english: "Corpus Callosum (Left)", latin: "Corpus Callosum (Sinister)" },
  "Corpus callosum_R": { english: "Corpus Callosum (Right)", latin: "Corpus Callosum (Dexter)" },
  "Dentate gyrus_L": { english: "Dentate Gyrus (Left)", latin: "Gyrus Dentatus (Sinister)" },
  "Dentate gyrus_R": { english: "Dentate Gyrus (Right)", latin: "Gyrus Dentatus (Dexter)" },
  "Fornix_L": { english: "Fornix (Left)", latin: "Fornix (Sinister)" },
  "Fornix_R": { english: "Fornix (Right)", latin: "Fornix (Dexter)" },
  "Fourth ventricle": { english: "Fourth Ventricle", latin: "Ventriculus Quartus" },
  "Globus pallidus lateral_L": { english: "Lateral Globus Pallidus (Left)", latin: "Globus Pallidus Lateralis (Sinister)" },
  "Globus pallidus lateral_R": { english: "Lateral Globus Pallidus (Right)", latin: "Globus Pallidus Lateralis (Dexter)" },
  "Globus pallidus medial_L": { english: "Medial Globus Pallidus (Left)", latin: "Globus Pallidus Medialis (Sinister)" },
  "Globus pallidus medial_R": { english: "Medial Globus Pallidus (Right)", latin: "Globus Pallidus Medialis (Dexter)" },
  "Hippocampus_L": { english: "Hippocampus (Left)", latin: "Hippocampus (Sinister)" },
  "Hippocampus_R": { english: "Hippocampus (Right)", latin: "Hippocampus (Dexter)" },
  "Hypothalamus": { english: "Hypothalamus", latin: "Hypothalamus" },
  "Indusium griseum_L": { english: "Indusium Griseum (Left)", latin: "Indusium Griseum (Sinister)" },
  "Indusium griseum_R": { english: "Indusium Griseum (Right)", latin: "Indusium Griseum (Dexter)" },
  "Lateral ventricle_L": { english: "Lateral Ventricle (Left)", latin: "Ventriculus Lateralis (Sinister)" },
  "Lateral ventricle_R": { english: "Lateral Ventricle (Right)", latin: "Ventriculus Lateralis (Dexter)" },
  "Medulla_oblongata_L": { english: "Medulla Oblongata (Left)", latin: "Medulla Oblongata (Sinister)" },
  "Medulla_oblongata_R": { english: "Medulla Oblongata (Right)", latin: "Medulla Oblongata (Dexter)" },
  "Midbrain_L": { english: "Midbrain (Left)", latin: "Mesencephalon (Sinister)" },
  "Midbrain_R": { english: "Midbrain (Right)", latin: "Mesencephalon (Dexter)" },
  "Pons_L": { english: "Pons (Left)", latin: "Pons (Sinister)" },
  "Pons_R": { english: "Pons (Right)", latin: "Pons (Dexter)" },
  "Putamen_L": { english: "Putamen (Left)", latin: "Putamen (Sinister)" },
  "Putamen_R": { english: "Putamen (Right)", latin: "Putamen (Dexter)" },
  "Septal nucleus_L": { english: "Septal Nucleus (Left)", latin: "Nucleus Septalis (Sinister)" },
  "Septal nucleus_R": { english: "Septal Nucleus (Right)", latin: "Nucleus Septalis (Dexter)" },
  "Thalamus_L": { english: "Thalamus (Left)", latin: "Thalamus (Sinister)" },
  "Thalamus_R": { english: "Thalamus (Right)", latin: "Thalamus (Dexter)" },
};

export function getBrainInfo(meshName) {
  if (!meshName) return { english: "Unknown", latin: "Unknown" };

  // 1. Try exact match
  if (BRAIN_NAMES[meshName]) return BRAIN_NAMES[meshName];

  // 2. Try normalized (space for underscore)
  const normalized = meshName.replace(/_/g, ' ');
  if (BRAIN_NAMES[normalized]) return BRAIN_NAMES[normalized];

  // 3. Fallback logic to expand L/R
  let clean = meshName.replace(/_/g, ' ');
  
  if (clean.endsWith(' L')) clean = clean.substring(0, clean.length - 2) + ' (Left)';
  else if (clean.endsWith(' R')) clean = clean.substring(0, clean.length - 2) + ' (Right)';
  else if (clean.endsWith('_L')) clean = clean.substring(0, clean.length - 2) + ' (Left)';
  else if (clean.endsWith('_R')) clean = clean.substring(0, clean.length - 2) + ' (Right)';

  return { english: clean, latin: clean };
}
